import React from 'react';
import { Icons } from '../ui';

// ============================================================================
// NINE QUESTIONS EXPLAINER - Modal explaining the framework
// ============================================================================
function NineQuestionsExplainer({ open, onClose, darkMode }) {
  const questions = [
    { num: 1, q: 'WHAT is the governance object?', desc: 'The name and description of what you\'re documenting', fields: ['name', 'description'], example: 'Cash Structuring Detection Scenario' },
    { num: 2, q: 'WHY was this value/approach chosen?', desc: 'Data-driven rationale. NOT "industry standard" or "per policy"', fields: ['regulatory_basis'], example: 'Based on 18-month analysis showing 94% of confirmed structuring between $3K-$9K', critical: true },
    { num: 3, q: 'WHO is accountable?', desc: 'Named individual, not a department', fields: ['owner_email'], example: 'sarah.chen@bank.com (BSA Officer)' },
    { num: 4, q: 'WHAT ELSE was considered?', desc: 'Alternatives evaluated and why rejected', fields: ['alternatives_considered'], example: '$5K threshold rejected due to 34% detection gap vs $3K' },
    { num: 5, q: 'WHERE is the documentation?', desc: 'Specific location of supporting evidence', fields: ['documentation_location'], example: 'SharePoint > BSA Program > Scenarios > Structuring > Analysis.xlsx' },
    { num: 6, q: 'Under what AUTHORITY?', desc: 'Regulatory citation or board approval', fields: ['authority_citation'], example: '31 CFR 1010.311; Board Resolution BR-2024-017' },
    { num: 7, q: 'What EVIDENCE proves implementation?', desc: 'Attached documentation, not assertions', fields: ['evidence_summary'], example: 'Attached: Alert-Statistics-Q4.pdf, Validation-Results.xlsx' },
    { num: 8, q: 'When is the next REVIEW?', desc: 'Scheduled reassessment date', fields: ['next_review_date', 'review_frequency'], example: 'Annual review, next: 2026-03-15' },
    { num: 9, q: 'TRAINING or competency requirements?', desc: 'Personnel responsible and their training/certification', fields: ['training_requirements', 'competency'], example: 'BSA Officer: CAMS certified; annual BSA/AML training' },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className={`w-full max-w-3xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col ${darkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white'} shadow-2xl`} onClick={e => e.stopPropagation()}>
        <div className={`px-6 py-4 border-b flex items-center justify-between ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <div>
            <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>The Nine Questions Framework</h2>
            <p className={`text-sm mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>Every governance decision should answer these questions</p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
            <Icons.X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className={`mb-4 p-3 rounded-lg ${darkMode ? 'bg-amber-900/30 border border-amber-700' : 'bg-amber-50 border border-amber-200'}`}>
            <p className={`text-sm font-medium ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
              💡 Question #2 (WHY) is the most important and most commonly failed during examinations.
            </p>
          </div>
          
          <div className="space-y-3">
            {questions.map(q => (
              <div key={q.num} className={`p-4 rounded-xl border ${q.critical ? (darkMode ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-200') : (darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200')}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${q.critical ? 'bg-amber-500 text-white' : (darkMode ? 'bg-slate-600 text-white' : 'bg-slate-300 text-slate-700')}`}>
                    {q.num}
                  </div>
                  <div className="flex-1">
                    <div className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{q.q}</div>
                    <div className={`text-sm mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-600'}`}>{q.desc}</div>
                    <div className={`text-xs mt-2 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      <span className="font-medium">Example:</span> {q.example}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className={`mt-6 p-4 rounded-xl ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
            <div className={`font-medium ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>Scoring</div>
            <div className={`text-sm mt-1 ${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
              Each answered question = 1 point. Aim for <strong>9/9</strong> for examiner readiness.
              The WHY field requires at least 50 characters of substantive rationale.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NineQuestionsExplainer;
